'use client';

import React, { Component, ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  componentName?: string;
}

interface State {
  hasError: boolean;
  error?: Error;
}

/**
 * Error boundary to catch and handle Plasmic component errors gracefully
 * Prevents Plasmic errors from crashing the entire app
 */
export class PlasmicErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Silently catch the error to prevent it from bubbling up
    // Log error to console in development for debugging
    if (process.env.NODE_ENV === 'development') {
      console.warn('Plasmic component error (caught and handled):', error, errorInfo);
    }
    
    // In production, you might want to log to an error reporting service
    // e.g., Sentry, LogRocket, etc.
    // But don't re-throw the error - we want to handle it gracefully
  }

  render() {
    if (this.state.hasError) {
      // Custom fallback UI if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Fail completely silently - render nothing
      // This allows the rest of the app to continue normally
      return null;
    }

    return this.props.children;
  }
}
